/**
 * Small formatting helpers shared by the calibration page panels.
 */
import type { CalibrationBasis } from '../../services/calibrations';

/** A naive UTC ISO time from the API (no Z) as a Date. */
export const utcNaiveToDate = (iso: string): Date =>
  new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);

/** Local time as the value a datetime-local input expects (YYYY-MM-DDTHH:mm). */
export const toLocalInputValue = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** API local-naive time ("2026-09-01T08:00:00") as a datetime-local value. */
export const apiLocalToInput = (iso?: string | null): string => (iso ? iso.slice(0, 16) : '');

/** Age as h:mm, the way lab sheets write it ("48:00"). */
export const formatAge = (hours?: number | null): string => {
  if (hours == null || !Number.isFinite(hours)) return '-';
  const sign = hours < 0 ? '-' : '';
  const total = Math.round(Math.abs(hours) * 60);
  return `${sign}${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** Age in days for the tooltip ("3.0 days"). */
export const formatAgeDays = (hours?: number | null): string =>
  hours == null || !Number.isFinite(hours) ? '' : `${(hours / 24).toFixed(1)} days`;

export const fmt1 = (v?: number | null): string => (v == null ? '-' : v.toFixed(1));

/** "Nurse-Saul TTF, datum 0 °C" / "Arrhenius equivalent age, Q 5000 K, reference 20 °C". */
export const describeBasis = (b?: CalibrationBasis | null): string => {
  if (!b) return 'Not recorded';
  if (b.method === 'Nurse Saul') {
    return b.datum_temp != null ? `Nurse-Saul TTF, datum ${b.datum_temp} °C` : 'Nurse-Saul TTF';
  }
  const parts = ['Arrhenius equivalent age'];
  if (b.q_value != null) parts.push(`Q ${b.q_value} K`);
  if (b.specified_temp != null) parts.push(`reference ${b.specified_temp} °C`);
  return parts.join(', ');
};

export const BASIS_SOURCE_TEXT: Record<string, string> = {
  snapshot: 'Recorded when the calibration was finished',
  calibration_pour: 'From the calibration pour',
  equation: 'Chosen for this calibration',
  mix_design: 'Assumed from the mix design. Choose the equation to confirm it.',
};

export const isSystemType = (type?: string | null): boolean => (type || '').trim().toLowerCase() === 's';
